import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  type BrandBookInput,
  type BrandBookSourceType,
  type BrandBookSummary,
  type BrandStatus,
  createBrandBook,
  deleteBrandBook,
  fetchBrandBooks,
  refreshBrandBookBrand,
  renameBrandBook,
} from '../api.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { describeError, redirectIfUnauthorised, useAppData } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { brandBookPath, Link } from '../router.tsx';
import { since } from '../schedule.ts';
import { useToast } from '../toast.tsx';
import { Badge, EmptyState, Notice, PageHeader, Skeleton, VisuallyHidden } from '../ui.tsx';

/** How often the list is re-read while any book is extracting. */
const POLL_MS = 3000;

/** Mirrors the server's name rule in `routes/brand-books.ts`. */
const MAX_NAME_LENGTH = 60;

/**
 * Brand books (US-011): every brand the app knows, where each one comes from,
 * and whether its profile is there, being extracted or broken.
 *
 * A repository or website book starts its first extraction the moment it is
 * created; the list keeps polling while any book reads `extracting`, so a
 * finished or failed run shows up without a reload.
 */
export function BrandBooks() {
  const toast = useToast();
  const [books, setBooks] = useState<BrandBookSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Refreshes this page started that the list may not show as running yet. */
  const [starting, setStarting] = useState<ReadonlySet<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<BrandBookSummary | null>(null);
  const [deleting, setDeleting] = useState<BrandBookSummary | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      setBooks(await fetchBrandBooks(signal));
      setLoadError(null);
    } catch (cause: unknown) {
      if (signal?.aborted === true || redirectIfUnauthorised(cause)) return;
      setLoadError(describeError(cause));
    }
  }, []);

  const isExtracting = (book: BrandBookSummary): boolean =>
    book.brandStatus === 'extracting' || starting.has(book.id);
  const anyExtracting = books?.some(isExtracting) ?? false;

  // One read on arrival, then another every few seconds while a run is going.
  useEffect(() => {
    if (books !== null && !anyExtracting) return;
    const controller = new AbortController();
    const timer = window.setTimeout(
      () => {
        void load(controller.signal);
      },
      books === null ? 0 : POLL_MS,
    );
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [load, books, anyExtracting]);

  /**
   * Starts a refresh without waiting on it: the request answers only when the
   * extraction is done, and the poll is what reports it. The id stays in
   * `starting` until then, so the row reads `extracting` from the first click.
   */
  const startRefresh = (book: Pick<BrandBookSummary, 'id' | 'name'>): void => {
    setStarting((current) => new Set(current).add(book.id));
    refreshBrandBookBrand(book.id)
      .then((result) => {
        if (result.status === 'failed') toast.error(`${book.name}: ${result.error ?? result.message}`);
        else toast.ok(`${book.name}: brand profile extracted.`);
      })
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        toast.error(`${book.name}: ${describeError(cause)}`);
      })
      .finally(() => {
        setStarting((current) => {
          const next = new Set(current);
          next.delete(book.id);
          return next;
        });
        void load();
      });
  };

  const onCreated = (book: BrandBookSummary): void => {
    setCreating(false);
    setBooks((current) =>
      [...(current ?? []).filter((other) => other.id !== book.id), book].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    );
    toast.ok(`Created ${book.name}.`);
    if (book.sourceType !== 'manual') startRefresh(book);
  };

  const onRenamed = (book: BrandBookSummary): void => {
    setRenaming(null);
    toast.ok(`Renamed to ${book.name}.`);
    void load();
  };

  const onDeleted = (book: BrandBookSummary): void => {
    setDeleting(null);
    toast.ok(`Deleted ${book.name}.`);
    void load();
  };

  return (
    <div className="page">
      <PageHeader
        title="Brand books"
        subtitle="The brands contents are made in: where each one comes from, and whether its profile is ready."
        actions={
          <button type="button" className="button button--primary" onClick={() => setCreating(true)}>
            <Icon name="plus" />
            New brand book
          </button>
        }
      />

      {loadError !== null && <Notice kind="error">{loadError}</Notice>}

      {books === null ? (
        loadError === null && (
          <div className="panel">
            <div className="panel__body">
              <Skeleton lines={4} />
            </div>
          </div>
        )
      ) : books.length === 0 ? (
        <EmptyState
          icon="paintbrush"
          title="No brand books yet"
          action={
            <button type="button" className="button button--primary" onClick={() => setCreating(true)}>
              <Icon name="plus" />
              Create the first one
            </button>
          }
        >
          A brand book holds what a product looks and sounds like. Extract it from a repository or a
          website, or type it by hand.
        </EmptyState>
      ) : (
        <div className="panel">
          <div className="panel__body">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Source</th>
                  <th scope="col">Status</th>
                  <th scope="col">Last extracted</th>
                  <th scope="col">Context items</th>
                  <th scope="col">Contents</th>
                  <th scope="col">
                    <VisuallyHidden>Actions</VisuallyHidden>
                  </th>
                </tr>
              </thead>
              <tbody>
                {books.map((book) => (
                  <BookRow
                    key={book.id}
                    book={book}
                    extracting={isExtracting(book)}
                    onRefresh={() => startRefresh(book)}
                    onRename={() => setRenaming(book)}
                    onDelete={() => setDeleting(book)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && <NewBrandBookDialog onCreated={onCreated} onCancel={() => setCreating(false)} />}
      {renaming !== null && (
        <RenameDialog key={renaming.id} book={renaming} onRenamed={onRenamed} onCancel={() => setRenaming(null)} />
      )}
      {deleting !== null && (
        <DeleteDialog key={deleting.id} book={deleting} onDeleted={onDeleted} onCancel={() => setDeleting(null)} />
      )}
    </div>
  );
}

/** `repository <name>` / `website <host>` / `typed by hand`. */
export function brandBookSource(book: BrandBookSummary): string {
  if (book.sourceType === 'manual') return 'typed by hand';
  if (book.sourceType === 'website') {
    if (book.websiteUrl === null) return 'website (no URL)';
    try {
      return `website ${new URL(book.websiteUrl).host}`;
    } catch {
      return `website ${book.websiteUrl}`;
    }
  }
  return book.repositoryName === null ? 'repository (deleted)' : `repository ${book.repositoryName}`;
}

function BookRow({
  book,
  extracting,
  onRefresh,
  onRename,
  onDelete,
}: {
  readonly book: BrandBookSummary;
  readonly extracting: boolean;
  readonly onRefresh: () => void;
  readonly onRename: () => void;
  readonly onDelete: () => void;
}) {
  return (
    <tr>
      <td>
        <Link className="link" href={brandBookPath(book.id)}>
          <strong>{book.name}</strong>
        </Link>
      </td>
      <td title={book.websiteUrl ?? undefined}>{brandBookSource(book)}</td>
      <td>
        <BookStatus book={book} extracting={extracting} />
      </td>
      <td title={book.brandExtractedAt ?? undefined}>
        {book.brandExtractedAt === null ? <span className="muted">never</span> : since(book.brandExtractedAt)}
      </td>
      <td className="num">{String(book.contextItemCount)}</td>
      <td className="num">{String(book.contentCount)}</td>
      <td>
        <span className="field__actions">
          <button
            type="button"
            className="button button--small"
            onClick={onRefresh}
            disabled={book.sourceType === 'manual' || extracting}
            title={book.sourceType === 'manual' ? 'A brand book typed by hand has nothing to extract from.' : undefined}
          >
            <Icon name="sync" />
            {extracting ? 'Extracting…' : 'Refresh'}
          </button>
          <button type="button" className="button button--small button--quiet" onClick={onRename}>
            <Icon name="pencil" />
            Rename
          </button>
          <button
            type="button"
            className="button button--small button--quiet button--danger"
            onClick={onDelete}
            aria-label={`Delete ${book.name}`}
          >
            <Icon name="trash" />
            Delete
          </button>
        </span>
      </td>
    </tr>
  );
}

function BookStatus({ book, extracting }: { readonly book: BrandBookSummary; readonly extracting: boolean }) {
  if (book.brandStatus === 'failed' && !extracting) {
    return (
      <div className="stack stack--tight">
        <BrandStatusBadge status="failed" />
        {book.brandError !== null && <span className="field__hint">{book.brandError}</span>}
      </div>
    );
  }
  return <BrandStatusBadge status={extracting ? 'extracting' : book.brandStatus} />;
}

/** A brand book's profile status as one badge, the same on every page. */
export function BrandStatusBadge({ status }: { readonly status: BrandStatus }) {
  if (status === 'extracting') {
    return (
      <Badge tone="active" pulse>
        extracting
      </Badge>
    );
  }
  if (status === 'failed') return <Badge tone="danger">failed</Badge>;
  if (status === 'ready') return <Badge tone="done">ready</Badge>;
  return <Badge tone="neutral">no profile</Badge>;
}

export function NewBrandBookDialog({
  onCreated,
  onCancel,
}: {
  readonly onCreated: (book: BrandBookSummary) => void;
  readonly onCancel: () => void;
}) {
  const { repositories } = useAppData();
  const [name, setName] = useState('');
  const [sourceType, setSourceType] = useState<BrandBookSourceType>('repository');
  const [repositoryId, setRepositoryId] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const input = ((): BrandBookInput | string => {
    const trimmed = name.trim();
    if (trimmed === '') return 'Give the brand book a name.';
    if (sourceType === 'repository') {
      return repositoryId === '' ? 'Pick a repository.' : { name: trimmed, sourceType, repositoryId };
    }
    if (sourceType === 'website') {
      return websiteUrl.trim() === ''
        ? 'Enter the website URL.'
        : { name: trimmed, sourceType, websiteUrl: websiteUrl.trim() };
    }
    return { name: trimmed, sourceType };
  })();

  const submit = (): void => {
    if (typeof input === 'string') {
      setError(input);
      return;
    }
    setBusy(true);
    setError(null);
    createBrandBook(input)
      .then(onCreated)
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      })
      .finally(() => setBusy(false));
  };

  return (
    <ConfirmDialog
      open
      title="New brand book"
      confirmLabel="Create brand book"
      busyLabel="Creating…"
      busy={busy}
      confirmDisabled={typeof input === 'string'}
      onConfirm={submit}
      onCancel={onCancel}
    >
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="field">
          <label className="field__label" htmlFor="brand-book-name">
            Name
          </label>
          <input
            id="brand-book-name"
            className="field__input"
            value={name}
            maxLength={MAX_NAME_LENGTH}
            onChange={(event) => setName(event.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="field">
          <span className="field__label">Source</span>
          <label className="radio">
            <input
              type="radio"
              name="brand-book-source"
              checked={sourceType === 'repository'}
              onChange={() => setSourceType('repository')}
            />
            A repository
          </label>
          {sourceType === 'repository' && (
            <select
              className="field__input"
              value={repositoryId}
              onChange={(event) => setRepositoryId(event.target.value)}
              aria-label="Repository"
            >
              <option value="">
                {repositories === null
                  ? 'Loading repositories…'
                  : repositories.length === 0
                    ? 'No repositories yet'
                    : 'Pick a repository'}
              </option>
              {(repositories ?? []).map((repository) => (
                <option key={repository.id} value={repository.id}>
                  {repository.name}
                </option>
              ))}
            </select>
          )}
          <label className="radio">
            <input
              type="radio"
              name="brand-book-source"
              checked={sourceType === 'website'}
              onChange={() => setSourceType('website')}
            />
            A website
          </label>
          {sourceType === 'website' && (
            <input
              className="field__input mono"
              type="url"
              value={websiteUrl}
              onChange={(event) => setWebsiteUrl(event.target.value)}
              placeholder="https://example.com"
              aria-label="Website URL"
              autoComplete="off"
              spellCheck={false}
            />
          )}
          <label className="radio">
            <input
              type="radio"
              name="brand-book-source"
              checked={sourceType === 'manual'}
              onChange={() => setSourceType('manual')}
            />
            Typed by hand
          </label>
          <p className="field__hint">
            {sourceType === 'manual'
              ? 'Nothing is extracted; you type the brand yourself.'
              : 'The first extraction starts as soon as the book is created.'}
          </p>
        </div>

        {error !== null && <Notice kind="error">{error}</Notice>}
      </form>
    </ConfirmDialog>
  );
}

function RenameDialog({
  book,
  onRenamed,
  onCancel,
}: {
  readonly book: BrandBookSummary;
  readonly onRenamed: (book: BrandBookSummary) => void;
  readonly onCancel: () => void;
}) {
  const [name, setName] = useState(book.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();
  const unchanged = trimmed === '' || trimmed === book.name;

  const submit = (): void => {
    if (unchanged) return;
    setBusy(true);
    setError(null);
    renameBrandBook(book.id, trimmed)
      .then(onRenamed)
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        setError(describeError(cause));
      })
      .finally(() => setBusy(false));
  };

  return (
    <ConfirmDialog
      open
      title={`Rename ${book.name}`}
      confirmLabel="Rename"
      busyLabel="Renaming…"
      busy={busy}
      confirmDisabled={unchanged}
      onConfirm={submit}
      onCancel={onCancel}
    >
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="field">
          <label className="field__label" htmlFor="brand-book-rename">
            Name
          </label>
          <input
            id="brand-book-rename"
            className="field__input"
            value={name}
            maxLength={MAX_NAME_LENGTH}
            onChange={(event) => setName(event.target.value)}
            autoComplete="off"
          />
        </div>
        {error !== null && <Notice kind="error">{error}</Notice>}
      </form>
    </ConfirmDialog>
  );
}

function DeleteDialog({
  book,
  onDeleted,
  onCancel,
}: {
  readonly book: BrandBookSummary;
  readonly onDeleted: (book: BrandBookSummary) => void;
  readonly onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  /** The server's refusal — for `in_use`, the sentence that names the count. */
  const [error, setError] = useState<string | null>(null);
  const [inUse, setInUse] = useState(false);

  const submit = (): void => {
    setBusy(true);
    setError(null);
    deleteBrandBook(book.id)
      .then(() => onDeleted(book))
      .catch((cause: unknown) => {
        if (redirectIfUnauthorised(cause)) return;
        const refused = cause instanceof ApiError && cause.code === 'in_use';
        setInUse(refused);
        setError(
          refused
            ? `${book.name} cannot be deleted: ${cause.message}`
            : describeError(cause),
        );
      })
      .finally(() => setBusy(false));
  };

  return (
    <ConfirmDialog
      open
      title={`Delete ${book.name}?`}
      confirmLabel="Delete brand book"
      busyLabel="Deleting…"
      busy={busy}
      confirmDisabled={inUse}
      danger
      onConfirm={submit}
      onCancel={onCancel}
    >
      <p>
        The brand book, its brand profile, its corrections and its context items — with their stored
        files — are removed from this server.
      </p>
      {error !== null && <Notice kind="error">{error}</Notice>}
    </ConfirmDialog>
  );
}
